import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development, could send to error tracking service in production
    console.error('Error Boundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // In production, you might want to send this to an error tracking service
    // Example: Sentry.captureException(error);
  }

  handleRefresh = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            bgcolor: '#F8FAFC',
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              maxWidth: 500,
              borderRadius: 4,
              border: '1px solid #E5E7EB',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: '50%',
                  bgcolor: '#FEF2F2',
                  border: '2px solid #FECACA',
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 48, color: '#EF4444' }} />
              </Box>
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1F2937', mb: 2 }}>
              Something went wrong
            </Typography>

            <Typography variant="body1" sx={{ color: '#6B7280', mb: 4 }}>
              We apologize for the inconvenience. An unexpected error has occurred.
              Please try refreshing the page or go back to the homepage.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={this.handleRefresh}
                sx={{
                  borderColor: '#D1D5DB',
                  color: '#374151',
                  '&:hover': {
                    borderColor: '#9CA3AF',
                    bgcolor: '#F3F4F6',
                  },
                }}
              >
                Refresh Page
              </Button>
              <Button
                variant="contained"
                onClick={this.handleGoHome}
                sx={{
                  background: 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #DB2777 0%, #7C3AED 100%)',
                  },
                }}
              >
                Go to Homepage
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
